// Pick Up in Store / Deliver to Address Functionalities
function setAddToCartDisabled(deliveryOptions, disabled) {
  const formId = deliveryOptions?.getAttribute("data-form");
  if (!formId) {
    return;
  }

  const buyButtons = document.querySelector(`buy-buttons[form="${CSS.escape(formId)}"]`);
  if (!buyButtons || buyButtons.getAttribute("data-in-stock") === "false") {
    return;
  }

  const addToCartButton = buyButtons.querySelector('[data-buy-buttons-content] button[type="submit"]');
  if (addToCartButton) {
    addToCartButton.disabled = disabled;
  }
}


function isMadeToOrderPurchasable(deliveryOptions) {
  const formId = deliveryOptions?.getAttribute("data-form");
  const buyButtons = formId && document.querySelector(`buy-buttons[form="${CSS.escape(formId)}"]`);
  return buyButtons?.getAttribute("data-made-to-order") === "true";
}

function relabelPickupRadio(deliveryOptions, storeName) {
  const pickupInput = deliveryOptions.querySelector('input[type="radio"][value="pickup"]');
  const pickupLabel = pickupInput && deliveryOptions.querySelector(`label[for="${CSS.escape(pickupInput.id)}"]`);
  if (pickupLabel) {
    const template = window.themeVariables?.strings?.deliveryOptionPickupAt || "Pick up at {{ store_name }}";
    pickupLabel.innerHTML = template.replace("{{ store_name }}", `<span class="store-name">${storeName}</span>`);    
  }
}

// Fallback if the "Stock lookup API endpoint" theme setting is blank.
const DEFAULT_PICKUP_OOS_API_ENDPOINT = "https://jennifer-fisher-app-node.vercel.app/api/inventory";

// The endpoint returns a GID (e.g. "gid://shopify/Location/123"); the stored location id is numeric.
function numericPickupLocationId(gid) {
  return typeof gid === "string" ? gid.split("/").pop() : null;
}


const inventoryRequestsByVariantId = new Map();

function fetchInventoryLocations(variantId) {
  if (inventoryRequestsByVariantId.has(variantId)) {
    return inventoryRequestsByVariantId.get(variantId);
  }

  const endpoint = window.themeVariables?.settings?.oosNotifyApiEndpoint || DEFAULT_PICKUP_OOS_API_ENDPOINT;
  let url;
  try {
    url = new URL(endpoint);
  } catch (error) {
    return Promise.resolve(null);
  }
  url.searchParams.set("variant_id", variantId);

  const request = fetch(url)
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .finally(() => inventoryRequestsByVariantId.delete(variantId));

  inventoryRequestsByVariantId.set(variantId, request);
  return request;
}

function showLoadingBarUntil(promise) {
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  promise.finally(() => {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  });
}

// Tells the customer this store doesn't have the product for pickup, and disables Add to Cart.
function showStoreUnavailableMessage(deliveryOptions, storeName) {
  const pickupInput = deliveryOptions.querySelector('input[type="radio"][value="pickup"]');
  const pickupLabel = pickupInput && deliveryOptions.querySelector(`label[for="${CSS.escape(pickupInput.id)}"]`);
  if (!pickupLabel || !storeName) {
    return;
  }

  const template =
    window.themeVariables?.strings?.deliveryOptionPickupStoreUnavailable ||
    "Pick up at {{ store_name }} - This product is not available at this store. Please select another store.";
    // pickupLabel.textContent = template.replace("{{ store_name }}", storeName);
    pickupLabel.innerHTML = template.replace("{{ store_name }}", `<span class="store-name">${storeName}</span>`);
    setAddToCartDisabled(deliveryOptions, true);
  }


function checkStoreAvailability(deliveryOptions, drawer, preference, variantIdOverride) {
  // variantIdOverride lets the caller pass the just-selected variant directly instead of reading it
  // off the drawer, which may not be updated yet.
  const variantId = variantIdOverride || drawer.querySelector("store-selection")?.getAttribute("data-variant-id");
  if (!variantId) {
    return;
  }

  if (isMadeToOrderPurchasable(deliveryOptions)) {
    relabelPickupRadio(deliveryOptions, preference.pickupLocationName);
    setAddToCartDisabled(deliveryOptions, false);
    return;
  }

   const validationKey = `${variantId}:${preference.pickupLocationId}`;
  if (deliveryOptions.dataset.pickupAvailabilityChecked === validationKey) {
    return;
  }
  deliveryOptions.dataset.pickupAvailabilityChecked = validationKey;

  fetchInventoryLocations(variantId).then((data) => {
    if (!data || !Array.isArray(data.locations)) {
      return;
    }

    const location = data.locations.find((entry) => numericPickupLocationId(entry.id) === preference.pickupLocationId);
    const available = !!location && location.sellable_quantity > 0;
    if (available) {
      relabelPickupRadio(deliveryOptions, preference.pickupLocationName);
      setAddToCartDisabled(deliveryOptions, false);
    } else {
      showStoreUnavailableMessage(deliveryOptions, preference.pickupLocationName);
    }
  });
}

class DeliveryOptions extends HTMLElement {
  connectedCallback() {
    this.addEventListener("change", (event) => this.handleSelection(event.target));

    this.addEventListener("click", (event) => {
      const input = event.target.closest('input[type="radio"]');
      if (input?.value === "pickup" && input.checked) {
        this.handleSelection(input);
      }
    });

    this.syncCheckedState();
    window.addEventListener("pageshow", () => this.syncCheckedState());
    window.addEventListener("load", () => this.syncCheckedState());
  }

  syncCheckedState() {
    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      this.setAttribute("data-delivery-method", checkedInput.value);
    }
  }

  handleSelection(input) {
    if (!input || input.type !== "radio" || !this.contains(input)) return;
    this.setAttribute("data-delivery-method", input.value);
    if (input.value === "address") {
      window.JFDeliveryPreference?.save({ method: "address" });
      setAddToCartDisabled(this, false);
    }

    this.dispatchEvent(
      new CustomEvent("delivery-method:change", {
        bubbles: true,
        detail: { method: input.value, form: this.getAttribute("data-form") },
      })
    );
  }
}

if (!window.customElements.get("delivery-options")) {
  window.customElements.define("delivery-options", DeliveryOptions);
}

// Shared by the PDP's own store-selection drawers (prefetched below) AND the cart's "Change Store"
// drawer (assets/pickup-in-store.js, cart-store-change section) - both fill in a <store-selection>
// container's stock-status dots the same way, off the same live inventory endpoint.
const FEW_IN_STOCK_THRESHOLD = 5;

function stockStatus(quantity) {
  const strings = window.themeVariables?.strings || {};

  if (quantity <= 0) {
    return { status: "out", label: strings.storeStockOut || "Out of stock" };
  }
  if (quantity < FEW_IN_STOCK_THRESHOLD) {
    return { status: "few", label: strings.storeStockFew || "Only a few in stock" };
  }
  return { status: "in", label: strings.storeStockIn || "In stock" };
}

function numericLocationId(gid) {
  return typeof gid === "string" ? gid.split("/").pop() : null;
}

function applyStockData(container, locations) {
  const quantityByLocationId = new Map(
    locations.map((location) => [numericLocationId(location.id), location.sellable_quantity])
  );

  container.querySelectorAll("[data-store-row]").forEach((row) => {
    const quantity = quantityByLocationId.get(row.getAttribute("data-location-id"));
    const statusEl = row.querySelector("[data-stock-status]");
    if (quantity === undefined || !statusEl) return;

    const { status, label } = stockStatus(quantity);
    statusEl.dataset.stockStatus = status;
    statusEl.querySelector("[data-stock-label]").textContent = label;
    statusEl.hidden = false;
  });
}

function loadStockData(container) {
  const variantId = container.getAttribute("data-variant-id");
  if (!variantId) {
    return Promise.resolve();
  }

  return fetchInventoryLocations(variantId).then((data) => {
    if (!data || !Array.isArray(data.locations)) return;
    applyStockData(container, data.locations);
  });
}

(function () {
  function prefetchStockData() {
    document.querySelectorAll("store-selection").forEach((container) => {
      if (container.hasAttribute("data-stock-loaded") || !container.querySelector("[data-store-row]")) {
        return;
      }
      if (container.closest(".delivery-options-wrapper")?.hidden) {
        return;
      }

      container.setAttribute("data-stock-loaded", "true");
      loadStockData(container);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", prefetchStockData);
  } else {
    prefetchStockData();
  }

  function findMainQuantitySelector(formId) {
    return Array.from(document.querySelectorAll("quantity-selector")).find(
      (selector) =>
        selector.querySelector(`input[form="${CSS.escape(formId)}"]`) && !selector.closest("[data-drawer-quantity-sync]")
    );
  }

  function findDrawerQuantitySelector(drawer) {
    return drawer?.querySelector(`[data-drawer-quantity-sync] quantity-selector`);
  }

  document.addEventListener("delivery-method:change", (event) => {
    if (event.detail?.method !== "pickup") {
      return;
    }

    const drawerId = event.target.getAttribute("data-store-selection-drawer");
    const drawer = drawerId && document.getElementById(drawerId);
    if (!drawer) {
      return;
    }

    const formId = event.detail?.form;
    const mainQuantitySelector = formId && findMainQuantitySelector(formId);
    const drawerQuantitySelector = findDrawerQuantitySelector(drawer);
    const drawerQuantityInput = drawerQuantitySelector?.querySelector("input");

    if (mainQuantitySelector && drawerQuantitySelector) {
      drawerQuantitySelector.quantity = mainQuantitySelector.quantity;
      if (drawerQuantityInput) {
        drawerQuantityInput.disabled = true;
      }
    } else if (drawerQuantityInput) {
      drawerQuantityInput.disabled = false;
    }

    const container = drawer.querySelector("store-selection");
    let pendingStockPromise = null;

    if (container) {
      if (!container.hasAttribute("data-stock-loaded")) {
        container.setAttribute("data-stock-loaded", "true");
        pendingStockPromise = loadStockData(container);
      } else {
        const variantId = container.getAttribute("data-variant-id");
        pendingStockPromise = variantId && inventoryRequestsByVariantId.get(variantId);
      }
    }

    if (pendingStockPromise) {
      showLoadingBarUntil(pendingStockPromise);
      pendingStockPromise.then(() => drawer.show());
    } else {
      drawer.show();
    }
  });

  document.addEventListener("change", (event) => {
    const syncWrapper = event.target.closest("[data-drawer-quantity-sync]");
    const drawerQuantitySelector = syncWrapper?.querySelector("quantity-selector");
    if (!drawerQuantitySelector) {
      return;
    }

    const formId = syncWrapper.getAttribute("data-drawer-quantity-sync");
    const mainQuantitySelector = formId && findMainQuantitySelector(formId);
    if (!mainQuantitySelector) {
      return;
    }

    mainQuantitySelector.quantity = drawerQuantitySelector.quantity;
  });

  function applySelectedStore(deliveryOptions, input) {
    const storeName = input.closest("[data-store-row]")?.getAttribute("data-location-name");
    if (!storeName) {
      return null;
    }

    deliveryOptions.dataset.pickupLocationId = input.value;
    deliveryOptions.dataset.pickupLocationName = storeName;
    relabelPickupRadio(deliveryOptions, storeName);
    setAddToCartDisabled(deliveryOptions, false);

    return storeName;
  }

  document.addEventListener("change", (event) => {
    const input = event.target.closest('store-selection input[type="radio"]');
    if (!input) {
      return;
    }

    const drawer = input.closest("x-drawer");
    drawer?.hide();

    const deliveryOptions =
      drawer?.id && document.querySelector(`delivery-options[data-store-selection-drawer="${CSS.escape(drawer.id)}"]`);
    if (!deliveryOptions) {
      return;
    }

    const storeName = applySelectedStore(deliveryOptions, input);
    if (storeName) {
      window.JFDeliveryPreference?.save({
        method: "pickup",
        pickupLocationId: input.value,
        pickupLocationName: storeName,
      });

      // Verify the store actually has stock, live - applySelectedStore only assumed it does.
      checkStoreAvailability(deliveryOptions, drawer, { pickupLocationId: input.value, pickupLocationName: storeName });
    }
  });

  document.addEventListener("delivery-preference:store-restored", (event) => {
    applySelectedStore(event.detail.deliveryOptions, event.detail.storeInput);
  });

  function syncAlreadySelectedStores(root) {
    (root || document).querySelectorAll("delivery-options[data-store-selection-drawer]").forEach((deliveryOptions) => {
      const drawerId = deliveryOptions.getAttribute("data-store-selection-drawer");
      const drawer = drawerId && document.getElementById(drawerId);
      const checkedInput = drawer?.querySelector('store-selection input[type="radio"]:checked');
      if (checkedInput) {
        applySelectedStore(deliveryOptions, checkedInput);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => syncAlreadySelectedStores());
  } else {
    syncAlreadySelectedStores();
  }

  window.addEventListener("pageshow", () => syncAlreadySelectedStores());
  window.addEventListener("load", () => syncAlreadySelectedStores());

  document.addEventListener("quantity-pair:change", (event) => {
    const { formId, isPair, label } = event.detail || {};
    const deliveryOptions = formId && document.querySelector(`delivery-options[data-form="${CSS.escape(formId)}"]`);
    const drawerId = deliveryOptions?.getAttribute("data-store-selection-drawer");
    const drawer = drawerId && document.getElementById(drawerId);
    if (!drawer) {
      return;
    }

    const drawerQuantitySelector = findDrawerQuantitySelector(drawer);
    if (drawerQuantitySelector && !findMainQuantitySelector(formId)) {
      drawerQuantitySelector.quantity = isPair ? 2 : 1;
    }

    const variantTitleElement = drawer.querySelector(".store-selection__product [data-variant-title]");
    const originalTitle = variantTitleElement?.getAttribute("data-original-variant-title") || "";
    if (!variantTitleElement) {
      return;
    }

    if (isPair) {
      const remainingParts = originalTitle.split(" / ").filter((part) => !/^(left|right) ear$/i.test(part.trim()));
      variantTitleElement.innerHTML = [label, ...remainingParts].join(' <span class="variant-separator">|</span> ');
    } else {
      variantTitleElement.innerHTML = originalTitle.replace(/ \/ /g, ' <span class="variant-separator">|</span> ');
    }
  });
})();


(function () {
  const STORAGE_KEY = "jf:last-delivery-choice";

  function loadPreference() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function savePreference(preference) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
    } catch (error) {
      // Storage disabled/unavailable (private browsing, quota, etc.) - the choice just won't carry
      // over to the next page, which is a safe degrade rather than a hard failure.
    }
    applyToAll();

  }

  window.JFDeliveryPreference = { save: savePreference };

  function applyAddress(deliveryOptions) {
    const addressInput = deliveryOptions.querySelector('input[type="radio"][value="address"]');
    if (addressInput) {
      addressInput.checked = true;
    }
    deliveryOptions.setAttribute("data-delivery-method", "address");
    setAddToCartDisabled(deliveryOptions, false);
  }

  function applyPickup(deliveryOptions, preference) {
    const pickupInput = deliveryOptions.querySelector('input[type="radio"][value="pickup"]');
    if (!pickupInput) {
      return;
    }

    pickupInput.checked = true;
    deliveryOptions.setAttribute("data-delivery-method", "pickup");

    const drawerId = deliveryOptions.getAttribute("data-store-selection-drawer");
    const drawer = drawerId && document.getElementById(drawerId);
    const storeRow =
      drawer && preference.pickupLocationId
        ? drawer.querySelector(`[data-store-row][data-location-id="${CSS.escape(preference.pickupLocationId)}"]`)
        : null;
    const storeInput = storeRow?.querySelector('input[type="radio"]');
    if (!storeInput) {
      showStoreUnavailableMessage(deliveryOptions, preference.pickupLocationName);
      return;
    }

    storeInput.checked = true;
    // Section 2 above owns the actual dataset-stashing + relabeling logic - ask it to do the same
    // work it does when the customer clicks a store row themselves.
    document.dispatchEvent(
      new CustomEvent("delivery-preference:store-restored", { detail: { deliveryOptions, storeInput } })
    );

    checkStoreAvailability(deliveryOptions, drawer, preference);
  }

  function applyToAll(root) {
    const preference = loadPreference();
    if (!preference) {
      return;
    }

    (root || document).querySelectorAll("delivery-options[data-form]").forEach((deliveryOptions) => {
      if (preference.method === "address") {
        applyAddress(deliveryOptions);
      } else if (preference.method === "pickup") {
        applyPickup(deliveryOptions, preference);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => applyToAll());
  } else {
    applyToAll();
  }

  window.addEventListener("pageshow", () => applyToAll());
  window.addEventListener("load", () => applyToAll());

  document.addEventListener(
    "product:rerender",
    (event) => {
      if (event.detail.productChange) {
        return;
      }

      const formId = event.target.getAttribute("id");
      const deliveryOptions = formId && document.querySelector(`delivery-options[data-form="${CSS.escape(formId)}"]`);
      const drawerId = deliveryOptions?.getAttribute("data-store-selection-drawer");
      const drawer = drawerId && document.getElementById(drawerId);
      const newDrawer = drawerId && event.detail.htmlFragment.querySelector(`#${CSS.escape(drawerId)}`);
      if (!deliveryOptions || !drawer || !newDrawer) {
        return;
      }

      const currentSummary = drawer.querySelector(".store-selection__product");
      const newSummary = newDrawer.querySelector(".store-selection__product");
      if (currentSummary && newSummary) {
        currentSummary.replaceWith(newSummary);
      }

      const currentContainer = drawer.querySelector("store-selection");
      const newContainer = newDrawer.querySelector("store-selection");
      if (!currentContainer || !newContainer) {
        return;
      }

      currentContainer.replaceWith(newContainer);

      const pickupLocationId = deliveryOptions.dataset.pickupLocationId;
      if (!pickupLocationId) {
        return;
      }

      const restoredInput = newContainer.querySelector(
        `[data-store-row][data-location-id="${CSS.escape(pickupLocationId)}"] input[type="radio"]`
      );

      if (restoredInput) {
        restoredInput.checked = true;
        return;
      }

      const pickupLocationName = deliveryOptions.dataset.pickupLocationName;
      delete deliveryOptions.dataset.pickupLocationId;
      delete deliveryOptions.dataset.pickupLocationName;

      if (deliveryOptions.getAttribute("data-delivery-method") === "pickup") {
        showStoreUnavailableMessage(deliveryOptions, pickupLocationName);
      }
    },
    true
  );

  document.addEventListener(
    "variant:change",
    (event) => {
      // event.target.id would return the hidden <input name="id"> instead of the form's id attribute
      // (see the same note in section 4 below) - event.target.getAttribute("id") isn't affected.
      const formId = event.target.getAttribute("id");
      const deliveryOptions = formId && document.querySelector(`delivery-options[data-form="${CSS.escape(formId)}"]`);
      const variantId = event.detail?.variant?.id;
      if (!deliveryOptions || !variantId) {
        return;
      }

      const drawerId = deliveryOptions.getAttribute("data-store-selection-drawer");
      const drawer = drawerId && document.getElementById(drawerId);
      const container = drawer?.querySelector("store-selection");
      if (container) {
        container.setAttribute("data-variant-id", variantId);
        container.removeAttribute("data-stock-loaded");
      }

      if (deliveryOptions.getAttribute("data-delivery-method") !== "pickup") {
        return;
      }

      const pickupLocationId = deliveryOptions.dataset.pickupLocationId;
      const pickupLocationName = deliveryOptions.dataset.pickupLocationName;
      if (!drawer || !pickupLocationId || !pickupLocationName) {
        return;
      }

      checkStoreAvailability(deliveryOptions, drawer, { pickupLocationId, pickupLocationName }, variantId);
    },
    true
  );
})();

(function () {
 
  const storeSelectionFetches = new Map();

  function fetchLiveStoreSelection(productUrl) {
    if (storeSelectionFetches.has(productUrl)) {
      return storeSelectionFetches.get(productUrl);
    }

    const url = new URL(productUrl, window.location.origin);
    const request = fetch(url)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) {
          return null;
        }
        const doc = new DOMParser().parseFromString(html, "text/html");
        return Array.from(doc.querySelectorAll("store-selection")).find(
          (element) => !element.hasAttribute("data-cart-store-selection")
        );      
      })
      .catch(() => null)
      .finally(() => storeSelectionFetches.delete(productUrl));

    storeSelectionFetches.set(productUrl, request);
    return request;
  }

  // The fetched rows carry ids/name scoped to the PDP's own drawer - rewritten here to this cart
  // drawer's own drawer/form ids so they can't collide with a copy of the same product's rows sitting
  // in an actually-open PDP drawer elsewhere in the DOM.
  function rewriteStoreRowIds(root, drawerId, formId) {
    root.querySelectorAll("[data-store-row]").forEach((row) => {
      const locationId = row.getAttribute("data-location-id");
      const input = row.querySelector('input[type="radio"]');
      const label = row.querySelector("label");
      if (!input || !locationId) {
        return;
      }

      const newId = `${drawerId}-${locationId}`;
      input.id = newId;
      input.name = `store-selection-${formId}`;
      label?.setAttribute("for", newId);
    });
  }

  function populateCartStoreSelection(container) {
    const productUrl = container.getAttribute("data-product-url");
    const drawerId = container.getAttribute("data-drawer-id");
    const formId = container.getAttribute("data-form-id");
    const currentLocationId = container.getAttribute("data-current-pickup-location-id");
    if (!productUrl) {
      return Promise.resolve();
    }

    return fetchLiveStoreSelection(productUrl).then((liveContainer) => {
      container.textContent = "";

      if (!liveContainer || !liveContainer.querySelector("[data-store-row]")) {
        const empty = document.createElement("p");
        empty.className = "text-subdued";
        empty.textContent = window.themeVariables?.strings?.storeSelectionEmpty || "No stores currently offer pickup for this item.";
        container.appendChild(empty);
        return;
      }

      rewriteStoreRowIds(liveContainer, drawerId, formId);

      liveContainer.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = !!currentLocationId && input.value === currentLocationId;
      });

      container.append(...liveContainer.children);

      // The copied rows' stock-status dots start hidden (same as the PDP's own, until its own
      // prefetch fills them in) - this container is never seen by that prefetch, so it's loaded here.
      return loadStockData(container);
    });
  }

  document.addEventListener(
    "click",
    (event) => {
      const trigger = event.target.closest(".line-item__pickup-badge-trigger");
      if (!trigger) {
        return;
      }

      const drawer = document.getElementById(trigger.getAttribute("aria-controls"));
      const container = drawer?.querySelector("[data-cart-store-selection]");
      if (!container) {
        return;
      }

      event.stopPropagation();

      // Always re-fetched (no "already rendered" shortcut) so the store list reflects live
      // availability every time this is opened, not just what it looked like the first time.
      showLoadingBarUntil(populateCartStoreSelection(container).finally(() => drawer.show()));
    },
    true
  );

  function updateCartPickupStore(locationId, locationName) {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const attributes = {
      delivery_method: "pickup",
      pickup_location_id: locationId,
      pickup_location_name: locationName,
    };

    return fetch(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes, sections: sectionsToBundle.join(",") }),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart) {
          return;
        }

        window.JFDeliveryPreference?.save({ method: "pickup", pickupLocationId: locationId, pickupLocationName: locationName });

        if (cart.sections) {
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:change", {
              bubbles: true,
              detail: { baseEvent: "cart-pickup-store:change", cart },
            })
          );
        } else {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        }
      })
      .catch(() => {});
  }

  document.addEventListener("change", (event) => {
    const input = event.target.closest('[data-cart-store-selection] input[type="radio"]');
    if (!input) {
      return;
    }

    const locationName = input.closest("[data-store-row]")?.getAttribute("data-location-name");
    if (!locationName) {
      return;
    }

    input.closest("x-drawer")?.hide();
    showLoadingBarUntil(updateCartPickupStore(input.value, locationName));
  });
})();


(function () {
  function currentDeliveryChoice(form) {
    const formId = form.getAttribute("id");
    const deliveryOptions = formId && document.querySelector(`delivery-options[data-form="${CSS.escape(formId)}"]`);

    const method = deliveryOptions ? deliveryOptions.getAttribute("data-delivery-method") : "address";

    if (method === "address") {
      return { delivery_method: "address", pickup_location_id: "", pickup_location_name: "" };
    }

    if (method === "pickup") {
      const locationId = deliveryOptions.dataset.pickupLocationId;
      const locationName = deliveryOptions.dataset.pickupLocationName;
      // No store chosen yet - leave the cart's existing delivery method attributes untouched rather
      // than saving an incomplete "pickup, but no store" state.
      if (!locationId || !locationName) {
        return null;
      }

      return { delivery_method: "pickup", pickup_location_id: locationId, pickup_location_name: locationName };
    }

    return null;
  }

  function saveDeliveryMethod(attributes) {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    fetch(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes, sections: sectionsToBundle.join(",") }),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart) {
          return;
        }

        // The common case: the bundled sections came back, so <cart-drawer>'s own "cart:change"
        // listener can re-render immediately from this response - no second request needed.
        if (cart.sections) {
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:change", {
              bubbles: true,
              detail: { baseEvent: "delivery-method:save", cart },
            })
          );
        } else {
          // Defensive fallback (e.g. <cart-drawer> wasn't mounted to bundle a section id for) -
          // slower, but still correct.
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        }
      })
      .catch(() => {});
  }

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.includes("/cart/add")) {
      return;
    }

    const attributes = currentDeliveryChoice(form);
    if (!attributes) {
      return;
    }

    document.addEventListener("cart:change", () => saveDeliveryMethod(attributes), { once: true });
  });
})();


(function () {
  function numericLocationId(gid) {
    return typeof gid === "string" ? gid.split("/").pop() : null;
  }

  // Both the cart-drawer and /cart page can have their own checkout button at once.
  function setCheckoutDisabled(disabled) {
    document.querySelectorAll('form button[type="submit"][name="checkout"]').forEach((button) => {
      button.disabled = disabled;
    });
  }

  // On failure, assume available rather than blocking checkout on a broken check.
  function isAvailableAtStore(variantId, pickupLocationId) {
    return fetchInventoryLocations(variantId).then((data) => {
      if (!data || !Array.isArray(data.locations)) {
        return true;
      }
      const location = data.locations.find((entry) => numericLocationId(entry.id) === pickupLocationId);
      return !!location && location.sellable_quantity > 0;
    });
  }

  function checkAll() {
    const elements = Array.from(document.querySelectorAll("[data-line-pickup-check]"));
    const hasSubscriptionPickupIssue = document.querySelectorAll("[data-subscription-pickup-check]").length > 0;

    if (elements.length === 0) {
      setCheckoutDisabled(hasSubscriptionPickupIssue);
      return;
    }

    Promise.all(
      elements.map((el) => {
        const variantId = el.getAttribute("data-variant-id");
        const pickupLocationId = el.getAttribute("data-pickup-location-id");
        if (!variantId || !pickupLocationId) {
          return true;
        }

        return isAvailableAtStore(variantId, pickupLocationId).then((available) => {
          el.hidden = available;
          return available;
        });
      })
    ).then((results) => setCheckoutDisabled(hasSubscriptionPickupIssue || !results.every(Boolean)));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => checkAll());
  } else {
    checkAll();
  }

  // Re-check whenever the cart's contents change.
  document.addEventListener("cart-drawer:refreshed", () => checkAll());
  document.addEventListener("shopify:section:load", () => checkAll());
})();
